import moment from 'moment';
import MediumUser from '../../../../types/MediumUser';
import { groupManagersAndNonManagers, indirectReportsOf } from '../../services/UserHierarchy';
import { StringUserProperty, UserSelectionFilterState, UserSelectionState, UserSelectionStaticState } from './types';

type FilterFn = (user: MediumUser) => boolean;

export const basePropertyFilters: StringUserProperty[] = ['employmentType', 'department', 'location', 'team', 'role'];

export function getUsersForSelection(allUsers: MediumUser[], selection: UserSelectionState): MediumUser[] {
    switch (selection.type) {
        case 'FILTER':
            return getUsersForSelectionFilter(allUsers, selection);
        case 'STATIC':
            return getUsersForSelectionStatic(allUsers, selection);
    }
}

function getUsersForSelectionFilter(
    allUsers: MediumUser[],
    { propertyFilters, hierarchyFilters, idFilters }: UserSelectionFilterState
) {
    const getPropertyFilter = getPropertyFilters(propertyFilters);

    const filters: FilterFn[] = [
        getPropertyFilter('location'),
        getPropertyFilter('role'),
        getPropertyFilter('department'),
        getPropertyFilter('team'),
        getPropertyFilter('employmentType'),
        getHiringDateFilter(propertyFilters.hiringDate),
        getHiringDateRelativeFilter(propertyFilters.hiringDateRelative),
        ...getHierarchyFilters(allUsers, hierarchyFilters),
    ];

    const includedIds = new Set(idFilters.included);
    const excludedIds = new Set(idFilters.excluded);
    return allUsers.filter(
        user => includedIds.has(user.id) || (!excludedIds.has(user.id) && filters.every(filterFn => filterFn(user)))
    );
}

function getPropertyFilters(propertyFilters: UserSelectionFilterState['propertyFilters']) {
    return (key: StringUserProperty): FilterFn => {
        const filterSet = new Set(propertyFilters[key]);
        if (!filterSet.size) {
            return () => true;
        }
        return user => {
            const prop = user[key];
            return prop !== null && filterSet.has(prop);
        };
    };
}

function getHiringDateFilter(hiringDate: UserSelectionFilterState['propertyFilters']['hiringDate']): FilterFn {
    if (!hiringDate) {
        return () => true;
    }
    return user =>
        !!user.hiringCalendarDate &&
        (!!hiringDate.from ? user.hiringCalendarDate >= hiringDate.from : true) &&
        (!!hiringDate.to ? user.hiringCalendarDate <= hiringDate.to : true);
}

function getHiringDateRelativeFilter(
    hiringDateRelative: UserSelectionFilterState['propertyFilters']['hiringDateRelative']
): FilterFn {
    if (!hiringDateRelative) {
        return () => true;
    }
    return user => {
        if (!user.hiringCalendarDate) {
            return false;
        }
        const date = moment().subtract(hiringDateRelative.count, hiringDateRelative.unit).format(moment.HTML5_FMT.DATE);
        return hiringDateRelative.beforeOrAfter === 'after'
            ? date <= user.hiringCalendarDate
            : date > user.hiringCalendarDate;
    };
}

function getHierarchyFilters(allUsers: MediumUser[], hierarchyFilters: UserSelectionFilterState['hierarchyFilters']) {
    const filters: FilterFn[] = [];

    if (hierarchyFilters.level) {
        const { managers, nonManagers } = groupManagersAndNonManagers(allUsers);
        const managerIds = new Set(managers.map(user => user.id));
        const nonManagerIds = new Set(nonManagers.map(user => user.id));
        filters.push(
            {
                MANAGER: (user: MediumUser) => managerIds.has(user.id),
                NON_MANAGER: (user: MediumUser) => nonManagerIds.has(user.id),
            }[hierarchyFilters.level]
        );
    }

    if (hierarchyFilters.reportsOf.length) {
        const managerIds = new Set(hierarchyFilters.reportsOf);
        const managers = allUsers.filter(user => managerIds.has(user.id));
        const reportIds = new Set(indirectReportsOf(allUsers, managers).map(user => user.id));
        filters.push(user => reportIds.has(user.id));
    }

    return filters;
}

function getUsersForSelectionStatic(allUsers: MediumUser[], selection: UserSelectionStaticState) {
    const userIds = new Set(selection.userIds);
    return allUsers.filter(user => userIds.has(user.id));
}

export function getActiveFilterCount(filters: UserSelectionFilterState): number {
    return [
        basePropertyFilters.reduce(
            (count, filter) =>
                count + (filters.propertyFilters[filter] === null || !filters.propertyFilters[filter].length ? 0 : 1),
            0
        ),
        !!filters.idFilters.excluded.length || !!filters.idFilters.excluded.length ? 1 : 0,
        !!filters.hierarchyFilters.reportsOf.length || filters.hierarchyFilters.level !== null ? 1 : 0,
        !!filters.propertyFilters.hiringDate ? 1 : 0,
        !!filters.propertyFilters.hiringDateRelative ? 1 : 0,
    ].reduce((a, b) => a + b, 0);
}
